import { Suspense } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useGetPublicEventQuery } from '@proaxiv/server-api'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useAppSelector } from '../store/hooks'
import { fromEventSummary } from './event/eventMapping'
import type { GetPublicEventResponse } from './event/eventMapping'
import { PublicEventView } from './event/PublicEventView'

/**
 * Public-facing event page — lives outside AppLayout, shows no PROAXIV/Triplo
 * branding. All visual identity comes from the event's own branding config.
 *
 * This component owns only the data logic (store/API lookup, loading, 404 guards);
 * the actual rendering is shared with the event editor's live preview via
 * {@link PublicEventView} so organizers see a true WYSIWYG result while editing.
 */
export function PublicEventPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const inviteEmail = searchParams.get('email')

  // Prefer the event from the Redux store when already loaded (e.g. organiser view).
  // Fall back to the API so direct URL navigation works for public visitors.
  const eventFromStore = useAppSelector((s) => s.events.list.find((e) => e.id === id))
  // Store data is authoritative (no API round-trip needed) once the event is published
  // or already flagged as a draft-preview.
  const haveStoreEvent =
    eventFromStore?.status === 'published' || eventFromStore?.draftPreview === true
  const {
    data: rawData,
    isLoading,
    isFetching,
    isUninitialized,
  } = useGetPublicEventQuery(
    { eventId: id ?? '' },
    // Always fetch from API for draft store events so we receive the backend's draftPreview flag.
    // Skip only for already-published store events (no extra info needed from the API).
    { skip: !id || (!!eventFromStore && eventFromStore.status === 'published') }
  )
  const rawResponse = rawData as GetPublicEventResponse | undefined
  // draftPreview is authoritative from the API response (backend verified org membership).
  // For store events that already carry draftPreview:true (e.g. set by a prior API call),
  // that field is also respected. Store-only draft events without the flag show 404 as usual.
  const draftPreview = rawResponse?.draftPreview === true || eventFromStore?.draftPreview === true
  // Use the store event for display (published or already flagged as draft-preview).
  // For all other cases fall through to the API response.
  const event =
    (eventFromStore?.status === 'published' || eventFromStore?.draftPreview
      ? eventFromStore
      : undefined) ??
    (rawResponse ? fromEventSummary(rawResponse.event, rawResponse.draftPreview) : undefined)
  // Until the request has actually settled (data or error), keep showing the loading
  // state rather than a premature 404 — RTK Query reports isLoading:false for the
  // brief moment before an uninitialized/in-flight query is reflected in the hook.
  const queryPending = !!id && !haveStoreEvent && (isUninitialized || isLoading || isFetching)

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (queryPending) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 text-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    )
  }

  // ── Not found / not published ─────────────────────────────────────────────────
  // Draft events are shown to authenticated org members (draftPreview = true); all other
  // non-published events — and any request without a valid org-member token — show 404.
  if (!event || (event.status !== 'published' && !draftPreview)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 text-white">
        <p className="text-5xl font-black tracking-tighter opacity-20">404</p>
        <p className="text-sm text-neutral-400">This event is not available.</p>
      </div>
    )
  }

  // The language switcher lives here in the page wrapper rather than inside PublicEventView,
  // because that view is also rendered inside the event editor's live-preview panel — a
  // viewport-fixed control belongs to the real public page only (TC-EVT-062).
  return (
    <>
      <div className="fixed top-4 right-4 z-40">
        <Suspense fallback={null}>
          <LanguageSwitcher align="right" />
        </Suspense>
      </div>
      <PublicEventView event={event} draftPreview={draftPreview} inviteEmail={inviteEmail} />
    </>
  )
}
